import type { ReactNode } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import clsx from 'clsx';
import { Card } from '@/components/ui/card/Card';
import { IconButton } from '@/components/ui/icon-button';
import { useTranslation } from '@/hooks/useTranslation';
import type { FeedbackRating, RecentPrediction } from '../type/ModelPredictionTypes';

export interface RecentPredictionsColumn {
  key: string;
  header: string;
  numeric?: boolean;
  render: (prediction: RecentPrediction) => ReactNode;
}

type Props = {
  predictions: RecentPrediction[];
  /** Overrides the default "Recent predictions" heading (e.g. signals runs). */
  title?: string;
  /** When provided, each row gets thumbs up/down; called with the row id. */
  onFeedback?: (predictionId: string, rating: FeedbackRating) => void;
  /** Overrides the value column (default: first one-value output, rounded). */
  formatValue?: (prediction: RecentPrediction) => string;
  valueHeader?: string;
  /** Columns added after the value (e.g. what a tool can still mill). */
  extraColumns?: RecentPredictionsColumn[];
};

function primaryValue(prediction: RecentPrediction): string {
  const scalar = Object.values(prediction.outputs).find((v) => v.length === 1);
  return scalar ? String(Math.round(scalar[0] * 10) / 10) : '—';
}

function modelLabel(prediction: RecentPrediction): string {
  const processName = prediction.context?.processName;
  if (processName !== undefined) return String(processName);
  const base = prediction.modelName ?? prediction.modelId;
  return prediction.modelVersion ? `${base} v${prediction.modelVersion}` : base;
}

/** Server-persisted predictions for the caller's company (newest first). */
export function RecentPredictionsCard({
  predictions,
  title,
  onFeedback,
  formatValue = primaryValue,
  valueHeader,
  extraColumns = [],
}: Props) {
  const { t } = useTranslation();
  if (predictions.length === 0) return null;

  return (
    <Card as="section" data-testid="recent-predictions">
      <h2 className="mb-3 text-sm font-semibold">
        {title ?? t('machineLearning.modelPrediction.recent.title')}
      </h2>
      <div className="max-h-64 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-card">
            <tr className="eyebrow border-b border-border text-left">
              <th className="px-2 py-1.5 font-semibold">{t('machineLearning.modelPrediction.recent.when')}</th>
              <th className="px-2 py-1.5 font-semibold">{t('machineLearning.modelPrediction.recent.tool')}</th>
              <th className="px-2 py-1.5 font-semibold">{t('machineLearning.modelPrediction.recent.model')}</th>
              <th className="px-2 py-1.5 text-right font-semibold">
                {valueHeader ?? t('machineLearning.modelPrediction.recent.value')}
              </th>
              {extraColumns.map((column) => (
                <th
                  key={column.key}
                  className={clsx('px-2 py-1.5 font-semibold', column.numeric && 'text-right')}
                >
                  {column.header}
                </th>
              ))}
              {onFeedback && <th className="px-2 py-1.5" aria-hidden />}
            </tr>
          </thead>
          <tbody>
            {predictions.map((prediction, i) => (
              <tr key={prediction.id ?? `${prediction.createdAt}-${i}`} className="border-b border-border/50">
                <td className="px-2 py-1.5 text-muted-foreground">
                  {new Date(prediction.createdAt).toLocaleString()}
                </td>
                <td className="px-2 py-1.5 font-mono text-xs">{prediction.toolId ?? '—'}</td>
                <td className="px-2 py-1.5 text-muted-foreground">{modelLabel(prediction)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{formatValue(prediction)}</td>
                {extraColumns.map((column) => (
                  <td
                    key={column.key}
                    className={clsx(
                      'px-2 py-1.5',
                      column.numeric ? 'text-right tabular-nums' : 'text-muted-foreground'
                    )}
                  >
                    {column.render(prediction)}
                  </td>
                ))}
                {onFeedback && (
                  <td className="px-2 py-1.5">
                    {prediction.id && (
                      <span className="flex items-center justify-end gap-1">
                        <IconButton
                          icon={ThumbsUp}
                          data-testid={`feedback-up-${i}`}
                          label={t('machineLearning.modelPrediction.recent.helpful')}
                          aria-pressed={prediction.feedbackRating === 'up'}
                          onClick={() => onFeedback(prediction.id!, 'up')}
                          className={
                            prediction.feedbackRating === 'up'
                              ? 'text-success [&_svg]:fill-current'
                              : 'text-muted-foreground'
                          }
                        />
                        <IconButton
                          icon={ThumbsDown}
                          data-testid={`feedback-down-${i}`}
                          label={t('machineLearning.modelPrediction.recent.notHelpful')}
                          aria-pressed={prediction.feedbackRating === 'down'}
                          onClick={() => onFeedback(prediction.id!, 'down')}
                          className={
                            prediction.feedbackRating === 'down'
                              ? 'text-destructive [&_svg]:fill-current'
                              : 'text-muted-foreground'
                          }
                        />
                      </span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
